'use client'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { AnalyticsEventName, repairImageKitUrl } from '@dupe/common/constants'
import { getResearchAuthor } from '@dupe/common/research-report'
import Layout from '~/src/components/Layout'
import useAnalytics from '~/src/hooks/useAnalytics'
import type { PublishedResearchHubPage } from '~/src/db/queryPublishedResearchHub'
import {
  CanvasHubLayout,
  StructuredHubLayout,
} from '~/src/components/reports/hubs/layouts'
import { HubSiblingBand } from '~/src/components/reports/hubs/kit'
import { SeoReportDisclosure } from '~/src/components/reports/SeoReportDisclosure'
import type {
  HubRenderContext,
  HubFaqSection,
  HubSection,
} from '~/src/components/reports/hubs/kit'
import {
  HubAnalyticsProvider,
  type HubAnalyticsCallbacks,
} from '~/src/components/reports/hubs/HubAnalyticsContext'
import {
  classifyEntryReferrer,
  readEntryRefParam,
} from '~/src/lib/classifyEntryReferrer'
import { createPublicUrl } from '~/src/lib/helpers'
import { serializeJsonLd } from '~/src/lib/reports/jsonLd'
import {
  buildCollectionPageJsonLd,
  buildCollectionPageParts,
  buildFaqPageJsonLd,
} from '~/src/lib/reports/schemaJsonLd'
import { useScrollEngagementTracking } from '~/src/lib/reports/useScrollEngagementTracking'

type AdminHubStatus =
  | 'draft'
  | 'published'
  | 'unpublished'
  | 'failed'
  | 'archived'

const HubAdminControls = dynamic(
  () =>
    import('~/src/components/reports/hubs/HubAdminControls').then(
      (m) => m.HubAdminControls
    ),
  { ssr: false }
)

export interface ReportHubRendererProps {
  hub: PublishedResearchHubPage & {
    status?: AdminHubStatus
    lastError?: string | null
    stale?: boolean
  }
  adminPreview?: boolean
}

function AdminHubPreviewBanner({
  hub,
}: {
  hub: ReportHubRendererProps['hub']
}) {
  const message = hub.lastError
    ? hub.lastError
    : hub.status === 'failed'
      ? 'Generation failed. The page below is the last retained draft and should not be published until regeneration succeeds.'
      : hub.stale
        ? 'Assignments changed since this draft was generated. Regenerate before publishing.'
        : null

  if (!message) return null

  const isError = hub.status === 'failed' || Boolean(hub.lastError)

  return (
    <div
      className={
        isError
          ? 'border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'
          : 'border-b border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700'
      }
    >
      <div className='mx-auto flex max-w-6xl flex-col gap-1 md:flex-row md:items-center md:justify-between'>
        <span className='font-semibold'>
          {isError ? 'Hub generation failed' : 'Hub draft is stale'}
        </span>
        <span className='text-current/80'>{message}</span>
      </div>
    </div>
  )
}

function getPrimaryReportForMeta(
  hub: ReportHubRendererProps['hub'],
  context: HubRenderContext
) {
  for (const reportId of hub.sourceReportIds) {
    const report = context.reportsById[reportId]
    if (report) return report
  }

  return Object.values(context.reportsById)[0] ?? null
}

function enrichContextWithPageMeta(
  hub: ReportHubRendererProps['hub'],
  context: HubRenderContext
): HubRenderContext {
  const primaryReport = getPrimaryReportForMeta(hub, context)
  const author = getResearchAuthor(primaryReport?.categorySlug)
  const sourceCount =
    context.sourceSummary?.sourcesReviewed ??
    context.sourceSummary?.sourceLinks.length ??
    null
  const reportCount =
    hub.sourceReportIds.length || Object.keys(context.reportsById).length

  return {
    ...context,
    pageMeta: {
      author: {
        name: author.name,
        role: author.bio,
        avatarUrl: author.avatarUrl,
      },
      lastGeneratedAt: hub.lastGeneratedAt ?? null,
      reportCount,
      sourceCount,
      sourceHref: sourceCount && sourceCount > 0 ? '#sources' : null,
    },
  }
}

function repairHubContextImages(context: HubRenderContext): HubRenderContext {
  let changed = false
  const productsById: HubRenderContext['productsById'] = Object.fromEntries(
    Object.entries(context.productsById).map(([id, product]) => {
      if (!product.image) return [id, product]
      const image = repairImageKitUrl(product.image)
      if (image === product.image) return [id, product]
      changed = true
      return [id, { ...product, image }]
    })
  )
  const reportsById: HubRenderContext['reportsById'] = Object.fromEntries(
    Object.entries(context.reportsById).map(([id, report]) => {
      if (!report.imageUrl) return [id, report]
      const imageUrl = repairImageKitUrl(report.imageUrl)
      if (imageUrl === report.imageUrl) return [id, report]
      changed = true
      return [id, { ...report, imageUrl }]
    })
  )

  if (!changed) return context
  return {
    ...context,
    productsById,
    reportsById,
  }
}

/**
 * Deterministic hero kicker pill. The label is brand furniture, not editorial
 * copy, so the renderer overrides whatever the generator produced based on the
 * hub kind: informational hubs read "Dupe Guide", transactional hubs read
 * "Dupe's Top Picks". (Never "Consumer Reports" -- that trades on a testing
 * organization's name for research we don't do; flagged in the June 2026
 * EEAT audit.)
 */
function hubHeroKicker(hubKind: string): string | undefined {
  if (hubKind === 'transactional') return "Dupe's Top Picks"
  if (hubKind === 'informational') return 'Dupe Guide'
  return undefined
}

export function ReportHubRenderer({
  hub,
  adminPreview = false,
}: ReportHubRendererProps) {
  const analytics = useAnalytics()
  const viewedRef = useRef(false)

  const sections = useMemo<HubSection[]>(
    () =>
      (hub.pageSpec.sections as HubSection[]).map((section) =>
        section.type === 'hero'
          ? { ...section, kicker: hubHeroKicker(hub.hubKind) ?? section.kicker }
          : section
      ),
    [hub.pageSpec.sections, hub.hubKind]
  )
  const rawContext = hub.renderContext as HubRenderContext
  const context = useMemo(
    () => enrichContextWithPageMeta(hub, repairHubContextImages(rawContext)),
    [hub, rawContext]
  )
  const canonicalPath = hub.seo.canonicalPath || `/reports/${hub.slug}`
  const canonicalUrl = hub.seo.canonicalUrl ?? createPublicUrl(canonicalPath)
  const productCount = Object.keys(context.productsById).length
  const reportCount = Object.keys(context.reportsById).length

  useEffect(() => {
    if (adminPreview) return
    if (viewedRef.current) return
    viewedRef.current = true
    // Effects only run post-hydration, so window is available and there is no
    // pages-router `router.isReady` gate to wait for.
    analytics.track(
      AnalyticsEventName.HubPageViewed,
      {
        hub_id: hub.id,
        hub_slug: hub.slug,
        hub_kind: hub.hubKind,
        cluster_slug: hub.clusterSlug,
        layout_mode: hub.layoutMode,
        section_count: sections.length,
        product_count: productCount,
        report_count: reportCount,
        entry_referrer: classifyEntryReferrer(readEntryRefParam()),
      },
      { sendToPlausible: true }
    )
  }, [
    adminPreview,
    analytics,
    hub.id,
    hub.slug,
    hub.hubKind,
    hub.clusterSlug,
    hub.layoutMode,
    sections.length,
    productCount,
    reportCount,
  ])

  const handlePageEngaged = useCallback(() => {
    analytics.track(
      AnalyticsEventName.HubPageEngaged,
      {
        hub_id: hub.id,
        hub_slug: hub.slug,
        hub_kind: hub.hubKind,
        cluster_slug: hub.clusterSlug,
      },
      { sendToPlausible: true }
    )
  }, [analytics, hub.id, hub.slug, hub.hubKind, hub.clusterSlug])

  useScrollEngagementTracking({
    enabled: !adminPreview,
    onEngaged: handlePageEngaged,
  })

  const handleProductClick = useCallback<
    HubAnalyticsCallbacks['onProductClick']
  >(
    (params) => {
      analytics.track(
        AnalyticsEventName.HubProductClicked,
        {
          hub_id: hub.id,
          hub_slug: hub.slug,
          hub_kind: hub.hubKind,
          product_id: params.productId,
          product_slug: params.productSlug,
          product_name: params.productName,
          outbound_url: params.outboundUrl,
          price: params.price,
          position: params.position,
          section_type: params.sectionType,
        },
        { sendToPlausible: true, sendToTinybird: true }
      )
    },
    [analytics, hub.id, hub.slug, hub.hubKind]
  )

  const handleReportClick = useCallback<HubAnalyticsCallbacks['onReportClick']>(
    (params) => {
      analytics.track(
        AnalyticsEventName.HubReportClicked,
        {
          hub_id: hub.id,
          hub_slug: hub.slug,
          hub_kind: hub.hubKind,
          report_id: params.reportId,
          report_slug: params.reportSlug,
          report_title: params.reportTitle,
          position: params.position,
        },
        { sendToPlausible: true }
      )
    },
    [analytics, hub.id, hub.slug, hub.hubKind]
  )

  const hubAnalytics = useMemo<HubAnalyticsCallbacks>(
    () => ({
      onProductClick: handleProductClick,
      onReportClick: handleReportClick,
    }),
    [handleProductClick, handleReportClick]
  )

  const handleSiblingNavigate = useCallback(() => {
    if (!hub.siblingHub) return
    analytics.track(
      AnalyticsEventName.HubSiblingClicked,
      {
        hub_id: hub.id,
        hub_slug: hub.slug,
        hub_kind: hub.hubKind,
        cluster_slug: hub.clusterSlug,
        sibling_slug: hub.siblingHub.slug,
        sibling_hub_kind: hub.siblingHub.hubKind,
      },
      { sendToPlausible: true }
    )
  }, [
    analytics,
    hub.id,
    hub.slug,
    hub.hubKind,
    hub.clusterSlug,
    hub.siblingHub,
  ])
  const collectionPageJsonLd = buildCollectionPageJsonLd({
    name: hub.title,
    description: hub.seo.description,
    url: canonicalUrl,
    parts: buildCollectionPageParts({
      title: hub.title,
      reports: Object.values(context.reportsById),
      publishedAt: hub.publishedAt,
      updatedAt: hub.updatedAt,
    }),
  })
  const faqPageJsonLd = buildFaqPageJsonLd(
    sections.flatMap((section) => {
      if (section.type !== 'faq') return []
      return (section as HubFaqSection).items
    })
  )

  return (
    <>
      {/* JSON-LD rendered as inline body scripts (valid for Google structured
          data) instead of next-seo's head-bound components, so the renderer
          works under both the App Router (/reports) and the pages-router
          admin previews. <head> tags (title/canonical/OG/robots) are owned by
          generateMetadata in src/app/reports/[...reportPath]/page.tsx. */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(collectionPageJsonLd),
        }}
      />
      {faqPageJsonLd && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(faqPageJsonLd),
          }}
        />
      )}
      <Layout hideBorders fullWidth>
        {adminPreview && <AdminHubPreviewBanner hub={hub} />}
        {hub.siblingHub && (
          <div className='mx-auto w-full max-w-6xl px-4 pt-6 md:px-10'>
            <HubSiblingBand
              sibling={hub.siblingHub}
              variant='top'
              onNavigate={handleSiblingNavigate}
            />
          </div>
        )}
        <div className='mx-auto w-full max-w-6xl px-4 pt-6 md:px-10'>
          <SeoReportDisclosure />
        </div>
        <HubAnalyticsProvider value={hubAnalytics}>
          {hub.layoutMode === 'canvas' ? (
            <CanvasHubLayout sections={sections} context={context} />
          ) : (
            <StructuredHubLayout
              sections={sections}
              context={context}
              hubKind={hub.hubKind}
            />
          )}
        </HubAnalyticsProvider>
        {hub.siblingHub && (
          <div className='mx-auto w-full max-w-6xl px-4 pb-16 md:px-10'>
            <HubSiblingBand
              sibling={hub.siblingHub}
              variant='bottom'
              onNavigate={handleSiblingNavigate}
            />
          </div>
        )}
      </Layout>
      <HubAdminControls hub={hub} />
    </>
  )
}
